<template>
  <section
    class="relative mt-4 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 pb-5 xl:px-8 xl:pb-6"
    :class="showCountdown ? 'pt-8' : 'pt-5 xl:pt-6'"
  >
    <scoreboard-countdown
      v-if="showCountdown && countdownTo"
      class="absolute right-5 -top-5 md:right-8 md:-top-6"
      :to="countdownTo"
      :key="countdownTo.toMillis()"
    >
      <template #label>
        <p class="flex items-center gap-x-1.5 md:justify-start md:pb-1 text-sm leading-none">
          <i class="fa-solid fa-clock text-sm md:text-base" aria-hidden="true" />
          <span>Auction ends in</span>
        </p>
      </template>
    </scoreboard-countdown>

    <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" :class="showCountdown ? 'md:pr-40' : ''">
      <h2 class="text-xl xl:text-2xl font-semibold tracking-tight text-boba-brown">
        {{ title }}
      </h2>
      <p v-if="bids.length" class="text-sm text-boba-grey-dark tabular-nums">
        {{ bids.length }} {{ bids.length === 1 ? noun : `${noun}s` }}
      </p>
    </div>

    <div
      v-if="!bids.length"
      class="mt-4 rounded-lg border border-dashed border-boba-grey bg-white/60 px-4 py-6 text-center"
    >
      <i class="fa-light fa-gavel text-2xl text-boba-grey-dark" aria-hidden="true" />
      <p class="mt-2 text-sm font-medium text-boba-brown">
        You haven't placed any {{ noun }}s on this auction.
      </p>
    </div>

    <div v-else class="mt-4 rounded-lg border border-boba-grey/50 bg-white overflow-hidden">
      <div
        class="hidden xl:grid gap-x-3 items-center px-5 py-2 bg-boba-sand-light/70 border-b border-boba-grey/50 text-xs font-semibold uppercase tracking-wide text-boba-grey-dark"
        :class="gridCols"
        aria-hidden="true"
      >
        <span>{{ lowBid ? "Max / ea" : "Price / ea" }}</span>
        <span>Qty</span>
        <span>{{ lowBid ? "Max total" : "Total" }}</span>
        <span>Placed</span>
        <span>Status</span>
        <span v-if="canCancel" class="sr-only">Cancel</span>
      </div>

      <ul class="divide-y divide-boba-grey/50">
        <li
          v-for="bid in bids"
          :key="`bid-${bid.id}`"
          :id="`bid-${bid.id}`"
          data-test="bid-row"
          class="relative grid grid-cols-1 gap-y-1 items-center pl-4 py-3.5 xl:gap-x-3 xl:px-5 xl:py-3.5 hover:bg-boba-sand-light/60 transition-colors"
          :class="[
            gridCols,
            canCancel ? 'pr-16 xl:pr-5' : 'pr-4',
            bid.bid_status === 'cancelled' ? 'text-boba-grey-dark' : 'text-boba-brown',
          ]"
        >
          <p class="text-2xl xl:text-lg font-semibold leading-tight tabular-nums whitespace-nowrap xl:col-start-1 xl:row-start-1">
            {{ $format(bid.bid_amount / 1e9) }}
            <span class="text-sm font-normal text-boba-grey-dark"
              >miles<span class="xl:hidden"> / ea</span></span
            >
          </p>

          <div class="row-span-3 xl:row-span-1 xl:col-start-5 xl:row-start-1 justify-self-end xl:justify-self-start pr-4 xl:pr-0" :class="canCancel ? 'absolute top-3.5 right-12 xl:static' : 'absolute top-3.5 right-0 xl:static'">
            <bid-status :listing="listing" :bid="bid" detail-class="hidden xl:inline" />
          </div>

          <div v-if="canCancel" class="absolute top-2 right-2 xl:static xl:col-start-6 xl:row-start-1 xl:justify-self-end">
            <button
              v-if="cancellable(bid)"
              data-test="cancel"
              type="button"
              class="inline-flex items-center justify-center h-11 w-11 rounded-full xl:h-9 xl:w-9 xl:rounded-lg border border-transparent xl:border-boba-grey/70 bg-transparent xl:bg-white text-boba-grey-dark hover:text-red-700 xl:hover:border-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700/60 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-boba-grey-dark xl:disabled:hover:border-boba-grey/70 transition-colors"
              :disabled="cancelling"
              title="Cancel bid"
              :aria-label="`Cancel bid of ${$format(bid.bid_amount / 1e9)} miles for ${bid.bid_quantity}`"
              @click="emit('cancel', bid)"
            >
              <i class="fa-regular fa-trash-can text-base xl:text-sm" aria-hidden="true" />
            </button>
          </div>

          <div class="col-start-1 flex flex-wrap items-baseline gap-x-1.5 text-base xl:contents">
            <p class="tabular-nums xl:col-start-2 xl:row-start-1 xl:text-base">
              <span class="text-boba-grey-dark xl:hidden">Qty</span>
              {{ $format(bid.bid_quantity) }}
            </p>
            <span class="text-boba-grey-dark xl:hidden" aria-hidden="true">·</span>
            <p class="tabular-nums whitespace-nowrap xl:col-start-3 xl:row-start-1 xl:text-base">
              <span class="text-boba-grey-dark xl:hidden">{{ lowBid ? "up to" : "" }}</span>
              {{ $format(fromBaseUnits(mulBaseUnits(bid.bid_amount, bid.bid_quantity))) }}
              miles
            </p>
          </div>

          <p class="col-start-1 xl:col-start-4 xl:row-start-1 text-sm text-boba-grey-dark whitespace-nowrap xl:whitespace-normal xl:leading-snug">
            <span class="xl:hidden">{{ lowBid ? "Placed " : "" }}</span
            >{{ DateTime.fromISO(bid.created_at).toFormat("MMM d, yyyy") }}<span class="xl:hidden">, </span><span class="xl:block">{{ DateTime.fromISO(bid.created_at).toFormat("h:mm a") }}</span>
          </p>
        </li>
      </ul>
    </div>

    <p
      v-if="error"
      role="alert"
      class="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
      <span>{{ error.message }}</span>
    </p>

    <p v-if="canCancel && bids.length" class="mt-3 text-xs text-boba-grey-dark">
      Nothing is charged until the auction closes. You can cancel an active bid
      before then.
    </p>
  </section>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import { DateTime } from "ts-luxon";
import { Errors, Listing, ListingBid } from "@/types";
import { fromBaseUnits, mulBaseUnits } from "@/util/miles";
import BidStatus from "@/components/Account/BidStatus.vue";
import ScoreboardCountdown from "@/components/ScoreboardCountdown.vue";

const props = withDefaults(
  defineProps<{
    listing: Listing;
    bids: ListingBid[];
    cancelling?: boolean;
    error?: Errors.CancelBidError | null;
  }>(),
  { cancelling: false, error: null },
);

const emit = defineEmits<{
  (e: "cancel", bid: ListingBid): void;
}>();

const lowBid = computed(() => props.listing.isLowBid());
const title = computed(() => (lowBid.value ? "Your bids" : "Purchases"));
const noun = computed(() => (lowBid.value ? "bid" : "purchase"));

/** Cancel is only possible on a running low-bid auction. */
const canCancel = computed(() => lowBid.value && !!props.listing.isActive());
const showCountdown = computed(() => canCancel.value);
const countdownTo = computed(() => props.listing.auctionEndsAt());

function cancellable(bid: ListingBid) {
  return canCancel.value && bid.bid_status === "active";
}

const gridCols = computed(() =>
  canCancel.value
    ? "xl:grid-cols-[minmax(6rem,1fr)_4rem_minmax(5.5rem,1fr)_minmax(7rem,1fr)_minmax(5rem,auto)_2.25rem]"
    : "xl:grid-cols-[minmax(6rem,1fr)_4rem_minmax(5.5rem,1fr)_minmax(7rem,1fr)_minmax(5rem,1fr)]",
);
</script>
